import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User, AuthHeaders } from '../types';
import {
  AUTH_EXPIRED_EVENT,
  USER_DATA_KEY,
  USER_TOKEN_KEY,
  clearSession,
  getTokenExpiry,
  isTokenExpired,
} from '../services/session';

const API_BASE_URL = '/api';

interface UserContextValue {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<User>;
  signup: (email: string, password: string) => Promise<User>;
  logout: () => void;
  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
  getAuthHeaders: () => AuthHeaders;
}

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: { children: ReactNode }) {
  // A token left in storage past its expiry is not a session. Clearing it here rather than on first
  // failed request is what stops the app from opening with an email in the header and admin actions
  // that can only 401.
  const [token, setToken] = useState<string | null>(() => {
    const stored = localStorage.getItem(USER_TOKEN_KEY);
    if (isTokenExpired(stored)) {
      clearSession();
      return null;
    }
    return stored;
  });
  const [user, setUser] = useState<User | null>(() => {
    const data = localStorage.getItem(USER_DATA_KEY);
    return data ? JSON.parse(data) : null;
  });

  // The API layer clears storage on a 401 and announces it; mirror that into React state so the
  // header and the admin controls drop back to logged-out in the same tick.
  useEffect(() => {
    const onExpired = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  // End the session the moment the token lapses, without waiting for a request to fail. A tab left
  // open overnight would otherwise still be advertising a login that died hours ago.
  useEffect(() => {
    if (!token) return;

    const expiresAt = getTokenExpiry(token);
    if (expiresAt === null) return;

    // setTimeout overflows past a signed 32-bit delay and would fire immediately.
    const delay = Math.min(Math.max(expiresAt - Date.now(), 0), 2_147_483_647);
    const timer = window.setTimeout(() => {
      clearSession();
      setToken(null);
      setUser(null);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [token]);

  const login = async (email: string, password: string): Promise<User> => {
    const resp = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ message: 'Login failed' }));
      throw new Error(err.message || 'Invalid credentials');
    }

    const authResponse = await resp.json();

    const userData: User = {
      id: authResponse.userId,
      email: authResponse.email,
      role: authResponse.role,
      username: authResponse.username,
    };

    localStorage.setItem(USER_TOKEN_KEY, authResponse.token);
    setToken(authResponse.token);

    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
    setUser(userData);

    return userData;
  };

  const signup = async (email: string, password: string): Promise<User> => {
    const resp = await fetch(`${API_BASE_URL}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ message: 'Signup failed' }));
      throw new Error(err.message || 'Signup failed');
    }

    const authResponse = await resp.json();

    const userData: User = {
      id: authResponse.userId,
      email: authResponse.email,
      role: authResponse.role,
      username: authResponse.username,
    };

    localStorage.setItem(USER_TOKEN_KEY, authResponse.token);
    setToken(authResponse.token);

    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
    setUser(userData);

    return userData;
  };

  const logout = () => {
    clearSession();
    setToken(null);
    setUser(null);
  };

  const isAuthenticated = () => {
    return !!token && !!user && !isTokenExpired(token);
  };

  const isAdmin = () => {
    return isAuthenticated() && user?.role === 'ADMIN';
  };

  const getAuthHeaders = (): AuthHeaders => {
    if (!token) return {};
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  const value: UserContextValue = {
    user,
    token,
    login,
    signup,
    logout,
    isAuthenticated,
    isAdmin,
    getAuthHeaders,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export default UserContext;
